// i18n
import { translateInComponent, translate } from 'infras/i18n';
import translations from './i18n.json';

import {
  FormEvent,
  SyntheticEvent,
  useContext,
  useEffect,
  useState,
} from 'react';
import { ViewerItem } from 'presentations/api/schema/user';

import { apiPath } from 'presentations/routes/path';
import { get, patch } from 'infras/rest_client';
import { useRequest } from 'presentations/views/hooks/request';
import { EventDetailItem } from 'presentations/api/schema/event';
import { EventTypeSettingItem } from 'presentations/api/schema/event_type_setting';
import { ErrorList } from 'domains/models';
import Workflow from 'domains/models/workflow';
import { WorkflowType } from 'domains/models/workflow/workflowType';
import { EventContactAndAnswersUpdateBody } from 'presentations/api/schema/event_contact';
import { AnswerListItem } from 'presentations/api/schema/answer';
import Email from 'domains/models/user/email';
import { isMobilePhone } from 'infras/validator';
import {
  DashboardPortalContext,
  setErrorMessage,
} from 'presentations/reducers/DashboardPortal';
import { getErrorMessage } from 'presentations/views/helpers/error';

interface Props {
  viewer: ViewerItem;
  event: EventDetailItem | null;

  email?: string | null;
  name?: string | null;
  ccEmails?: string | null;
  phoneNumber?: string | null;
  textRemindCountry?: any | null;
  textRemindNumber?: string | null;
  company?: string | null;
  onUpdated?: any;
}

export function useContactEditForm(props: Props) {
  const t = translateInComponent(translations);
  const { state, dispatch } = useContext(DashboardPortalContext);
  const [name, setName] = useState<string | null>(null);
  const [email, setEmail] = useState<string | null>(null);
  const [company, setCompany] = useState<string | null>(null);
  const [ccEmails, setCcEmails] = useState<string[]>([]);
  const [inputingTag, setInputingTag] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [phoneNumber, setPhoneNumber] = useState<string | null>(null);
  const [textRemindCountry, setTextRemindCountry] = useState<any | null>(null);
  const [textRemindNumber, setTextRemindNumber] = useState<string | null>(null);

  const [initialized, setInitialized] = useState<boolean>(false);
  const [
    eventTypeSetting,
    setEventTypeSetting,
  ] = useState<EventTypeSettingItem | null>(null);
  const [isDisabled, setIsDisabled] = useState<boolean>(true);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [errors, setErrors] = useState<ErrorList>({});
  const [isShowResendConfirm, setIsShowResendConfirm] = useState<boolean>(
    false,
  );
  const [isAnyFieldChanged, setIsAnyFiledChanged] = useState<boolean>(false);

  const eventTypeRequest = useRequest(
    props.event
      ? apiPath(
          `/v1/teams/${props.viewer.team!.uid}/event_types/${
            props.event.eventTypeId
          }`,
        )
      : null,
  );
  const eventType = eventTypeRequest?.data;
  const form = eventType?.form;

  // カスタム質問
  const [answers, setAnswers] = useState<AnswerListItem[]>([]);
  const customQuestions: Workflow[] = form?.workflows || [];

  useEffect(() => {
    if (!initialized && props.event?.eventTypeId && props.event?.contact) {
      fetchSetting(props.event.eventTypeId);
      setName(props.event.contact.name);
      setEmail(props.event.contact.email);
      setCompany(props.event.contact.company);
      setCcEmails(
        props.event.contact.ccEmails
          ? props.event.contact.ccEmails.split(',')
          : [],
      );
      setMessage(props.event.contact.message);
      setPhoneNumber(props.event.contact.phoneNumber);
      setTextRemindCountry(props.event.contact.textRemindCountryCode);
      setTextRemindNumber(props.event.contact.textRemindNumber);
      try {
        setAnswers(JSON.parse(JSON.stringify(props.event.answers)));
      } catch (error) {
        console.log('failed load answers');
      }
      setInitialized(true);
    }
  }, [props.event, initialized]);

  const fetchSetting = async (eventTypeId: number) => {
    const result = await get(
      apiPath(
        `/v1/teams/${
          props.viewer.team!.uid
        }/event_types/${eventTypeId}/settings`,
      ),
    );
    if (!result.isSuccess) {
      return;
    }
    setEventTypeSetting(result.data);
  };

  const onSubmit = async (
    e: SyntheticEvent,
    isSendUpdates: boolean,
    updateMessage?: string,
  ) => {
    if (!props.event) {
      return;
    }

    setIsLoading(true);
    const contactUpdateBody: EventContactAndAnswersUpdateBody = {
      contact: {
        name: name ?? undefined,
        email: email ?? undefined,
        company: company ?? undefined,
        ccEmails: ccEmails ?? undefined,
        message: message ?? undefined,
        phoneNumber: phoneNumber ?? undefined,
        textRemindCountryCode: textRemindCountry?.dialCode
          ? '+' + textRemindCountry.dialCode
          : null,
        textRemindNumber: textRemindNumber ?? undefined,
      },
      answers,
      isSendUpdates,
      updateMessage,
    };
    const result = await patch(
      apiPath(
        `/v1/teams/${props.event.teamId}/events/${props.event.uid}/contact_answers/update`,
      ),
      { data: contactUpdateBody },
    );
    if (!result.isSuccess) {
      setIsLoading(false);
      dispatch(setErrorMessage(translate(getErrorMessage(result))));
      return;
    }

    if (props.onUpdated) {
      props.onUpdated();
    }
    setIsLoading(false);
    onCloseResendConfirmDialog();
  };

  const onChange = (e: any) => {
    // setIsDisabled(false);
    const { name, value } = e.currentTarget;
    const updateValue = value;
    switch (name) {
      case 'name':
        setName(updateValue);
        break;
      case 'email':
        setEmail(updateValue);
        break;
      case 'company':
        setCompany(updateValue);
        break;
      case 'message':
        setMessage(updateValue);
        break;
      case 'phoneNumber':
        setPhoneNumber(updateValue);
        break;
      default:
        if (typeof name === 'string' && name.startsWith('customQuestion')) {
          const index = Number(name.split('.')[1]);
          const newAnswers = [...answers];
          const customQuestion = customQuestions[index];

          if (
            customQuestion.workflowType ===
            WorkflowType.QuestionAnswerCheckboxes
          ) {
            const content = newAnswers[index].content;
            if (content && Array.isArray(content)) {
              const choiceValues = customQuestion.choices?.map((c) => c.value);
              const isClickOtherOption =
                choiceValues && !choiceValues.includes(updateValue);
              if (isClickOtherOption) {
                // その他が押された時
                const wasOtherOptionChecked =
                  content.length > 0 &&
                  content.some((c) => !choiceValues.includes(c));
                if (wasOtherOptionChecked) {
                  // その他がチェック状態から解除状態になった時
                  const newContent = [];
                  for (const c of content) {
                    if (choiceValues.includes(c)) {
                      newContent.push(c);
                    }
                  }
                  newAnswers[index].content = newContent;
                } else {
                  // その他が解除状態からチェック状態になった時
                  content.push(updateValue);
                }
              } else {
                if (content.includes(updateValue)) {
                  content.splice(content.indexOf(updateValue), 1);
                } else {
                  content.push(updateValue);
                }
              }
            }
          } else {
            newAnswers[index].content = updateValue;
          }

          setAnswers(newAnswers);
        }
        break;
    }
    setIsAnyFiledChanged(true);
  };

  const onChangeOtherAnswer = (e: any) => {
    setIsDisabled(false);
    const { name, value } = e.currentTarget;
    if (typeof name === 'string' && name.startsWith('customQuestion')) {
      const index = Number(name.split('.')[1]);
      const newAnswers = [...answers];
      const customQuestion = customQuestions[index];
      if (
        customQuestion.workflowType ===
        WorkflowType.QuestionAnswerMultipleChoice // ラジオボタンのケース
      ) {
        // その他のみが選択される場合はstringで保存する
        newAnswers[index].content = value;
        setAnswers(newAnswers);
      } else if (
        customQuestion.workflowType === WorkflowType.QuestionAnswerCheckboxes
      ) {
        let content = newAnswers[index].content;

        if (content && Array.isArray(content)) {
          const choiceValues = customQuestion.choices?.map((c) => c.value);
          const newContent = [];
          for (const c of content) {
            if (choiceValues?.includes(c)) {
              newContent.push(c);
            } else {
              newContent.push(value);
            }
            newAnswers[index].content = newContent;
          }
        } else {
          content = value;
        }
        setAnswers(newAnswers);
      }
      setIsAnyFiledChanged(true);
    }
  };

  const onChangeTextRemindNumber = (value: string, country: any) => {
    if (value.slice(country.dialCode.length)) {
      setIsDisabled(false);
      const internalPhoneNumber = value.slice(country.dialCode.length);
      setTextRemindNumber(internalPhoneNumber);
      setTextRemindCountry(country);
    } else {
      setIsDisabled(true);
      setTextRemindNumber(null);
      setTextRemindCountry(country);
    }

    const newErrors: ErrorList = { ...errors };
    delete newErrors['textRemindNumber'];
    setErrors(newErrors);
    setIsAnyFiledChanged(true);
    setIsDisabled(Object.keys(newErrors).length > 0);
  };

  const onInputChangeTag = async (e: any, value: string) => {
    setInputingTag(value);
  };

  const onSubmitTag = async (e: any) => {
    if (inputingTag) {
      const newEmails = [...ccEmails, inputingTag];
      setCcEmails(newEmails);
      setInputingTag(null);

      const newErrors = validate('ccEmails', newEmails);
      setIsDisabled(Object.keys(newErrors).length > 0);
      setErrors(newErrors);
      setIsAnyFiledChanged(true);
    }
  };

  const onDeleteTag = async (deleteIndex: number) => {
    const newEmails = [...ccEmails];
    newEmails.splice(deleteIndex, 1);
    setCcEmails(newEmails);

    const newErrors = validate('ccEmails', newEmails);
    setIsDisabled(Object.keys(newErrors).length > 0);
    setErrors(newErrors);
    setIsAnyFiledChanged(true);
  };

  const validate = (name: string, value: any): ErrorList => {
    const newErrors: ErrorList = {
      ...errors,
    };
    switch (name) {
      case 'email':
        const emailErrors: string[] = [];
        if (!value) {
          emailErrors.push('model.EventContact.email.E001');
        }
        const newEmail = new Email(value);
        if (!newEmail.isValid()) {
          emailErrors.push('model.EventContact.email.E002');
        }
        newErrors['email'] = emailErrors;
        if (emailErrors.length === 0) {
          delete newErrors['email'];
        }
        break;
      case 'name':
        const nameErrors: string[] = [];
        if (!value) {
          nameErrors.push('model.EventContact.name.E001');
        }
        newErrors['name'] = nameErrors;
        if (nameErrors.length === 0) {
          delete newErrors['name'];
        }
        break;
      case 'company':
        const companyErrors: string[] = [];
        if (
          eventType.setting.isCompanyRequired &&
          !eventType.setting.isCompanyHidden &&
          !value
        ) {
          companyErrors.push('model.EventContact.company.E002');
        }
        newErrors['company'] = companyErrors;
        if (companyErrors.length === 0) {
          delete newErrors['company'];
        }
        break;
      case 'textRemindNumber':
        if (!eventType.setting.isTextRemind) {
          break;
        }
        const textRemindNumberErrors: string[] = [];
        let textRemindFormatNumber;
        if (textRemindCountry && textRemindNumber) {
          textRemindFormatNumber = '+' + textRemindCountry.dialCode;
          if (textRemindNumber[0] === '0') {
            textRemindFormatNumber += textRemindNumber.slice(1);
          } else {
            textRemindFormatNumber += textRemindNumber;
          }
        }
        if (
          eventType.setting.isTextRemindNumberRequired &&
          !textRemindFormatNumber
        ) {
          textRemindNumberErrors.push(
            'model.EventContact.textRemindNumber.E002',
          );
        }
        if (textRemindFormatNumber && !isMobilePhone(textRemindFormatNumber)) {
          textRemindNumberErrors.push(
            'model.EventContact.textRemindNumber.E001',
          );
        }

        newErrors['textRemindNumber'] = textRemindNumberErrors;
        if (textRemindNumberErrors.length === 0) {
          delete newErrors['textRemindNumber'];
        }
        break;
      case 'ccEmails':
        const ccEmailsErrors: string[] = [];
        if (
          eventTypeSetting &&
          eventTypeSetting.isCcEmailsRequired &&
          value &&
          value.length === 0
        ) {
          ccEmailsErrors.push('model.EventContact.ccEmail.E001');
        }
        newErrors['ccEmails'] = ccEmailsErrors;
        if (ccEmailsErrors.length === 0) {
          delete newErrors['ccEmails'];
        }
        break;
      default:
        break;
    }
    return newErrors;
  };

  const onSubmitForm = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (isAnyFieldChanged) {
      setIsShowResendConfirm(true);
    } else {
      props.onUpdated();
    }
  };

  const onCloseResendConfirmDialog = () => {
    setIsShowResendConfirm(false);
  };

  return {
    state: {
      form,
      name,
      email,
      company,
      ccEmails,
      message,
      phoneNumber,
      textRemindCountry,
      textRemindNumber,
      customQuestions,
      isLoading,
      eventTypeSetting,
      isShowResendConfirm,
      answers,
    },
    actions: {
      onSubmit,
      onChange,
      onDeleteTag,
      onSubmitTag,
      onInputChangeTag,
      onChangeOtherAnswer,
      onChangeTextRemindNumber,
      onSubmitForm,
      onCloseResendConfirmDialog,
    },
  };
}
